import { Alert, Box } from '@mui/material';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { VERIFY_ACCOUNT_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { LoadingButton } from '@mui/lab';
import { IDateObject, DefaultDateObject, convertToJsDate } from '@acorn-portal/shared/src/utils/formDateValueUtils';
import { useEffect, useState } from 'react';
import { CustomerVerificationClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { AxiosResponse } from 'axios';
import { Check, ErrorOutline } from '@mui/icons-material';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { useAuth } from 'react-oidc-context';
import { formatISO } from 'date-fns';
import {
	DEFAULT_VERIFICATION_NUMBER,
	STREETCOVER_CONTACT_NUMBER,
} from '@acorn-portal/shared/src/utils/constants/index';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IVerifyAccountFormValues {
	dateOfBirth: IDateObject;
	vehicleRegistrationNumber: string;
}

const VERIFY_ACCOUNT_FORM_INITIAL_VALUES: IVerifyAccountFormValues = {
	dateOfBirth: DefaultDateObject,
	vehicleRegistrationNumber: '',
};

const verifyAccountFormValidationSchema = Yup.object().shape({
	dateOfBirth: Yup.object().shape({
		day: Yup.string().required(VERIFY_ACCOUNT_ERRORS.dateOfBirth),
		month: Yup.string().required(VERIFY_ACCOUNT_ERRORS.dateOfBirth),
		year: Yup.string().required(VERIFY_ACCOUNT_ERRORS.dateOfBirth),
	}),
	vehicleRegistrationNumber: Yup.string().required(VERIFY_ACCOUNT_ERRORS.vehicleRegistrationNumber),
});

const VerifyCustomerForm = () => {
	const auth = useAuth();
	const [formState, setFormState] = useState({
		loading: false,
		notFound: false,
		error: false,
	});

	const company = import.meta.env.VITE_AUTH_REALM?.toUpperCase();
	const contactNumber = company === 'STREETCOVER' ? STREETCOVER_CONTACT_NUMBER : DEFAULT_VERIFICATION_NUMBER;

	useEffect(() => {
		if (auth.user?.profile.isVerified) {
			location.reload();
		}
	}, [auth.user?.profile.isVerified]);

	const handleSubmitForm = async (
		values: IVerifyAccountFormValues,
		formik: FormikHelpers<IVerifyAccountFormValues>,
	) => {
		setFormState({
			loading: true,
			notFound: false,
			error: false,
		});

		const client = new CustomerVerificationClient(undefined, API);
		const dobISO = formatISO(convertToJsDate(values.dateOfBirth));
		await client
			.verifyCustomer({
				dob: dobISO,
				vehicleRegistrationNumber: values.vehicleRegistrationNumber,
			})
			.then(() => {
				// If the verification is successful, we need to update the user profile
				auth.signinSilent();
			})
			.catch((error: AxiosResponse) => {
				if (error.status === 404) {
					setFormState({
						loading: false,
						notFound: true,
						error: false,
					});
				} else if (error.status === 400) {
					formik.setErrors({
						vehicleRegistrationNumber: VERIFY_ACCOUNT_ERRORS.vehicleRegistrationNumber,
					});
					setFormState({
						loading: false,
						notFound: false,
						error: false,
					});
				} else {
					setFormState({
						loading: false,
						notFound: false,
						error: true,
					});
				}
			});
	};

	return (
		<Formik<IVerifyAccountFormValues>
			initialValues={VERIFY_ACCOUNT_FORM_INITIAL_VALUES}
			onSubmit={handleSubmitForm}
			validationSchema={verifyAccountFormValidationSchema}
		>
			{(formik) => (
				<Form>
					<FormikLogErrors />
					<Box
						sx={{
							margin: 'auto',
							maxWidth: '800px',
							textAlign: 'center',
						}}
					>
						{formState.notFound && (
							<Alert
								severity='error'
								icon={<ErrorOutline fontSize='large' />}
								sx={() => ({
									borderWidth: '2px',
									borderColor: 'error.main',
									borderStyle: 'solid',
									borderRadius: 2,
									marginBottom: 3,
									textAlign: 'left',
									alignContent: 'center',
									'> .MuiAlert-message': {
										alignContent: 'center',
									},
								})}
							>
								Your policy details could not be found. Please try again or call us on{' '}
								<b>
									<a href={`tel:${contactNumber}`}>{contactNumber}</a>
								</b>{' '}
								for assistance.
							</Alert>
						)}

						{formState.error && (
							<Alert
								severity='error'
								icon={<ErrorOutline fontSize='large' />}
								sx={() => ({
									borderWidth: '2px',
									borderColor: 'error.main',
									borderRadius: 2,
									borderStyle: 'solid',
									marginBottom: 3,
									textAlign: 'left',
									'> .MuiAlert-message': {
										alignContent: 'center',
									},
								})}
							>
								There has been an error when verifying your details, please contact our chat team for assistance.
							</Alert>
						)}

						<DatePickerField
							id='dateOfBirth'
							onChange={(value) => formik.setFieldValue('dateOfBirth', value)}
							label='Date of Birth'
							value={formik.values.dateOfBirth}
							error={
								(formik.touched.dateOfBirth?.day && Boolean(formik.errors.dateOfBirth?.day)) ||
								(formik.touched.dateOfBirth?.month && Boolean(formik.errors.dateOfBirth?.month)) ||
								(formik.touched.dateOfBirth?.year && Boolean(formik.errors.dateOfBirth?.year))
							}
							helperText={
								(formik.touched.dateOfBirth?.day && formik.errors.dateOfBirth?.day) ||
								(formik.touched.dateOfBirth?.month && formik.errors.dateOfBirth?.month) ||
								(formik.touched.dateOfBirth?.year && formik.errors.dateOfBirth?.year)
							}
						/>
						<FormTextField
							id='vehicleRegistrationNumber'
							name='vehicleRegistrationNumber'
							label='Vehicle Registration'
							value={formik.values.vehicleRegistrationNumber}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.vehicleRegistrationNumber && Boolean(formik.errors.vehicleRegistrationNumber)}
							helperText={formik.touched.vehicleRegistrationNumber && formik.errors.vehicleRegistrationNumber}
						/>
						<Box textAlign='center'>
							<LoadingButton
								variant='contained'
								color='success'
								fullWidth
								size='large'
								type='submit'
								loading={formState.loading}
								sx={{ maxWidth: { md: '425px' }, ml: { md: 7 } }}
								startIcon={<Check />}
							>
								Verify
							</LoadingButton>
						</Box>
					</Box>
				</Form>
			)}
		</Formik>
	);
};

export default VerifyCustomerForm;
